import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LgmButton from '../LgmButton';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { PutProductMultiResult } from '../../services/api/domains/products/types';
import { useForkAllProducts } from '../../services/api/domains/products/hooks';
import { GRAY_200, GRAY_50 } from 'theme/grayScale';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';

interface MultiResultModalProps {
  multiResult: PutProductMultiResult;
  originalProductId: number;
  manufacturerId: number;
  /** Unverified path only — user selected a single CAS RN to re-PUT with. */
  onSelectOne: (casRn: string) => void;
  onForkAllSuccess: () => void;
  onCancel: () => void;
}

/**
 * MultiResultModal — Task 6.1 (FR-6 / FR-6a)
 *
 * Shown when PUT /products/:id returns status:'multi_result': the product's
 * new man_product_name maps to N > 1 distinct CAS entities.
 *
 * Verified path (orphanCount present): Cancel or Fork All only.
 * Unverified path (orphanCount absent): Cancel, Select One, or Fork All.
 *
 * Renders with zIndex 20000 to stack above the ProductModal overlay (z-index 10000).
 */
const MultiResultModal: React.FC<MultiResultModalProps> = ({
  multiResult,
  originalProductId,
  manufacturerId,
  onSelectOne,
  onForkAllSuccess,
  onCancel,
}) => {
  const { candidates, orphanCount } = multiResult;
  const isVerifiedPath = orphanCount !== undefined;
  const n = candidates.length;

  const [selectedRn, setSelectedRn] = useState<string>('');

  const forkAllMutation = useForkAllProducts();
  const isForkAllPending = forkAllMutation.status === 'loading';

  // Back-button dismiss: stacks correctly on top of ProductModal and respects
  // the existing isForkAllPending close-disable state, mirroring the
  // `onClose={isForkAllPending ? undefined : onCancel}` MUI wiring below.
  useModalBackDismiss({
    isOpen: true,
    onClose: onCancel,
    canDismiss: () => !isForkAllPending,
  });

  const forkButtonLabel =
    orphanCount === 1
      ? `Create ${n} New Products + Delete Original`
      : `Create ${n} New Products`;

  const handleForkAll = async () => {
    try {
      await forkAllMutation.mutate({
        productId: originalProductId,
        data: { manufacturerId, candidates },
      });
      onForkAllSuccess();
    } catch (err) {
      console.error('❌ Fork All failed:', err);
      alert('Failed to create new products. Please try again.');
    }
  };

  const handleSelectOne = () => {
    if (selectedRn) {
      onSelectOne(selectedRn);
    }
  };

  return (
    <Dialog
      open
      onClose={isForkAllPending ? undefined : onCancel}
      sx={{ zIndex: 20000 }}
      slotProps={{ paper: { sx: { minWidth: 480, maxWidth: 600 } } }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Multiple Registry Matches Found
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          The new name maps to <strong>{n} distinct chemical entities</strong>{' '}
          in the registry. A single best match cannot be determined
          automatically.
        </Typography>

        {/* Candidate list — radio buttons for unverified, plain list for verified */}
        <div
          style={{
            background: GRAY_50,
            border: `1px solid ${GRAY_200}`,
            borderRadius: 6,
            padding: '8px 16px',
          }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{
              mb: 0.5,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
            Registry matches
          </Typography>

          {isVerifiedPath ? (
            /* Verified path — informational list only */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {candidates.map(c => (
                <div key={c.cas_number}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.product_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CAS {c.cas_number}
                  </Typography>
                </div>
              ))}
            </div>
          ) : (
            /* Unverified path — radio group for selection */
            <RadioGroup
              value={selectedRn}
              onChange={e => setSelectedRn(e.target.value)}>
              {candidates.map(c => (
                <FormControlLabel
                  key={c.cas_number}
                  value={c.cas_number}
                  disabled={isForkAllPending}
                  control={<Radio size="small" />}
                  label={
                    <span>
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={600}>
                        {c.product_name}
                      </Typography>{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary">
                        — CAS {c.cas_number}
                      </Typography>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          )}
        </div>

        {!isVerifiedPath && (
          <Typography variant="body2">
            <strong>Select one</strong> to save this product enriched with that
            entry&apos;s registry data. Or use{' '}
            <strong>Create {n} New Products</strong> to fork a separate product
            record for each match.
          </Typography>
        )}

        {isVerifiedPath && (
          <Typography variant="body2">
            Verified products cannot be reassigned to an ambiguous name. Use{' '}
            <strong>{forkButtonLabel}</strong> to create a separate product
            record for each match and move this manufacturer&apos;s link
            accordingly.
          </Typography>
        )}

        {orphanCount === 1 && (
          <div
            style={{
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 6,
              padding: '10px 16px',
            }}>
            <Typography variant="body2" color="warning.dark">
              <strong>The original product will be permanently deleted.</strong>{' '}
              This is its only manufacturer link — once moved to the new
              products, nothing will remain and it will be removed
              automatically.
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '12px 16px', gap: 1 }}>
        <LgmButton
          variant="flat"
          onClick={onCancel}
          disabled={isForkAllPending}>
          Cancel
        </LgmButton>

        {/* Select one — unverified path only */}
        {!isVerifiedPath && (
          <LgmButton
            variant="outlined"
            onClick={handleSelectOne}
            disabled={!selectedRn || isForkAllPending}>
            Save with Selected
          </LgmButton>
        )}

        <Button
          onClick={handleForkAll}
          loading={isForkAllPending}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#059669',
            '&:hover': { backgroundColor: '#047857' },
          }}>
          {forkButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiResultModal;
